import React, { useState, useRef, RefObject } from 'react'
import clsx from 'clsx'

import { Loader } from 'components'

export type ListTab = {
  title: string
  content: JSX.Element | string
  disabled: boolean
}

export type TabPaneProps = {
  isLoading: boolean
  content: JSX.Element | string
}

export type TabsProps = {
  list: ListTab[]
  defaultActiveKey: number
  isLoading: boolean
}

const Tabs: React.FC<TabsProps> = (props: TabsProps) => {
  const { list, defaultActiveKey = 1, isLoading } = props

  const [activeKey, setActiveKey] = useState(Number(defaultActiveKey) - 1)
  const contentRef = useRef<HTMLDivElement>(null)

  const base = 'tabs'

  const renderTabPanel = ({ isLoading, content }: TabPaneProps) => {
    const base = 'tab-pane'

    return (
      <div
        id={`${base}__layout`}
        className={clsx(
          `${base}__layout`,
          isLoading && `${base}__layout--loading`,
        )}
        ref={contentRef}
      >
        {!isLoading ? (
          <span className={`${base}__content`}>{content}</span>
        ) : (
          <Loader size='lg' />
        )}
      </div>
    )
  }

  const handleClickMenu = (
    key: number,
    contentRef: RefObject<HTMLDivElement>,
  ) => {
    const contentRefCurrent = contentRef.current

    setActiveKey(key)

    /*** TODO: Update later when more than 2 tabs are ready and it's a DAY-2 requirement. ***/
    if (contentRefCurrent) {
      switch (key) {
        case 0:
          setTimeout(() => {
            contentRefCurrent.scrollIntoView({
              behavior: 'smooth',
            })
            contentRefCurrent.scrollTo(0, 0)
          }, 500)
          break

        default:
          break
      }
    }
  }

  const renderMenu = (
    title: string,
    isDisabled: boolean,
    isActive: boolean,
    index: number,
    contentRef: RefObject<HTMLDivElement>,
  ) => {
    return (
      !isDisabled && (
        <div
          data-testid={`title-menu-${index}`}
          className={clsx(
            `${base}__title-menu`,
            isActive && `${base}__title-menu--active`,
          )}
          onClick={() => handleClickMenu(index, contentRef)}
        >
          <span
            className={clsx(
              `${base}__title-menu-text`,
              isActive && `${base}__title-menu-text--active`,
            )}
          >
            {title}
          </span>
        </div>
      )
    )
  }

  const renderContent = (
    isActive: boolean,
    isLoading: boolean,
    key: string | number,
    content: JSX.Element | string,
  ) => {
    return isActive && <>{renderTabPanel({ isLoading, content })}</>
  }

  return (
    <div className={`${base}__layout`}>
      {list.map((item, index) => (
        <div key={index}>
          {renderMenu(
            item.title,
            item.disabled,
            Boolean(activeKey === index),
            index,
            contentRef,
          )}

          {renderContent(
            Boolean(Number(activeKey) === index),
            isLoading,
            index,
            item.content,
          )}
        </div>
      ))}
    </div>
  )
}

export default Tabs
